import { useState } from "react";

import { useReferentialCallback } from "@north-beam/nb-client-side/hooks";

export enum DisclosureAction {
  OPEN = "open",
  CLOSE = "close",
  TOGGLE = "toggle",
}

export type DisclosureState = "opened" | "closed";

type UseDisclosureChangeParams = {
  readonly open: boolean;
  readonly state: DisclosureState;
  readonly action: DisclosureAction;
};

export interface UseDisclosureOptions {
  readonly onChange?: (params: UseDisclosureChangeParams) => void;
}

export const useDisclosure = (
  initialValue = false,
  options?: UseDisclosureOptions,
): [
  boolean,
  {
    state: DisclosureState;
    open: () => void;
    close: () => void;
    toggle: () => void;
  },
] => {
  const [open, _setOpen] = useState(initialValue);
  const state = open ? "opened" : "closed";

  const applyChange = useReferentialCallback((action: DisclosureAction) => {
    const valueMap: { [key in DisclosureAction]: boolean } = {
      [DisclosureAction.CLOSE]: false,
      [DisclosureAction.OPEN]: true,
      [DisclosureAction.TOGGLE]: !open,
    };
    _setOpen(valueMap[action]);
    options?.onChange?.({ open: valueMap[action], state, action });
  });

  return [
    open,
    {
      state: state,
      open: () => applyChange(DisclosureAction.OPEN),
      close: () => applyChange(DisclosureAction.CLOSE),
      toggle: () => applyChange(DisclosureAction.TOGGLE),
    },
  ];
};
